import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ExportModal = ({ onClose, onExport, totalRecords }) => {
  const [exportConfig, setExportConfig] = useState({
    format: 'csv',
    dateRange: 'current',
    fields: {
      timestamp: true,
      clientName: true,
      userName: true,
      actionPerformed: true,
      moduleUsed: true,
      ipAddress: true,
      status: true,
      severity: true,
      details: true
    },
    includeHeaders: true,
    fileName: `activity-logs-${new Date()?.toISOString()?.split('T')?.[0]}`
  });

  const handleFieldToggle = (field) => {
    setExportConfig({
      ...exportConfig,
      fields: {
        ...exportConfig?.fields,
        [field]: !exportConfig?.fields?.[field]
      }
    });
  };

  const handleExport = () => {
    onExport(exportConfig);
  };

  const selectedFieldsCount = Object.values(exportConfig?.fields)?.filter(Boolean)?.length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto elevation-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-foreground font-heading flex items-center gap-2">
              <Icon name="Download" size={24} className="text-primary" />
              Export Activity Logs
            </h2>
            <p className="text-sm text-muted-foreground font-caption mt-1">
              Configure export settings for {totalRecords} log records
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-smooth"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Export Format */}
          <div>
            <label className="block text-sm font-medium text-foreground font-body mb-3">
              Export Format
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setExportConfig({ ...exportConfig, format: 'csv' })}
                className={`flex items-center gap-3 p-4 border rounded-lg transition-smooth ${
                  exportConfig?.format === 'csv' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="FileText" size={20} />
                <div className="text-left">
                  <p className="text-sm font-semibold font-heading">CSV</p>
                  <p className="text-xs text-muted-foreground font-caption">Comma-separated values</p>
                </div>
              </button>
              <button
                onClick={() => setExportConfig({ ...exportConfig, format: 'pdf' })}
                className={`flex items-center gap-3 p-4 border rounded-lg transition-smooth ${
                  exportConfig?.format === 'pdf' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="FileText" size={20} />
                <div className="text-left">
                  <p className="text-sm font-semibold font-heading">PDF</p>
                  <p className="text-xs text-muted-foreground font-caption">Portable document</p>
                </div>
              </button>
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-foreground font-body mb-3">
              Date Range
            </label>
            <select
              value={exportConfig?.dateRange}
              onChange={(e) => setExportConfig({ ...exportConfig, dateRange: e?.target?.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth"
            >
              <option value="current">Current Filter Results</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last7days">Last 7 Days</option>
              <option value="last30days">Last 30 Days</option>
              <option value="last90days">Last 90 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>

          {/* Field Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-foreground font-body">
                Select Fields to Export
              </label>
              <span className="text-xs text-muted-foreground font-caption">
                {selectedFieldsCount} of 9 selected
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {Object.keys(exportConfig?.fields)?.map((field) => (
                <label
                  key={field}
                  className="flex items-center gap-2 p-3 border border-border rounded-md hover:bg-muted cursor-pointer transition-smooth"
                >
                  <input
                    type="checkbox"
                    checked={exportConfig?.fields?.[field]}
                    onChange={() => handleFieldToggle(field)}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary/50"
                  />
                  <span className="text-sm text-foreground font-body capitalize">
                    {field?.replace(/([A-Z])/g, ' $1')?.trim()}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Options */}
          <div>
            <label className="block text-sm font-medium text-foreground font-body mb-3">
              Additional Options
            </label>
            <label className="flex items-center gap-2 p-3 border border-border rounded-md hover:bg-muted cursor-pointer transition-smooth">
              <input
                type="checkbox"
                checked={exportConfig?.includeHeaders}
                onChange={(e) => setExportConfig({ ...exportConfig, includeHeaders: e?.target?.checked })}
                className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary/50"
              />
              <div>
                <p className="text-sm text-foreground font-body">Include Column Headers</p>
                <p className="text-xs text-muted-foreground font-caption">Add field names as first row</p>
              </div>
            </label>
          </div>

          {/* File Name */}
          <div>
            <label className="block text-sm font-medium text-foreground font-body mb-2">
              File Name
            </label>
            <input
              type="text"
              value={exportConfig?.fileName}
              onChange={(e) => setExportConfig({ ...exportConfig, fileName: e?.target?.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-smooth"
              placeholder="Enter file name"
            />
            <p className="text-xs text-muted-foreground font-caption mt-1">
              File will be saved as: {exportConfig?.fileName}.{exportConfig?.format}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border rounded-md text-sm font-medium text-foreground hover:bg-muted transition-smooth"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={selectedFieldsCount === 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth elevation-sm"
          >
            <Icon name="Download" size={16} />
            Export {exportConfig?.format?.toUpperCase()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;